import "./sign-submit.scss";

import { DialogController } from "aurelia-dialog";
import { autoinject, computedFrom } from "aurelia-framework";
import {
    validateTrigger,
    ValidationController,
    ValidationControllerFactory,
    ValidationRules
} from "aurelia-validation";
import moment from "moment";

import { IDialogOptions } from "../../../interfaces/i-dialog";
import nameof from "../../../common/nameof";
import { IGetCustomVolunteerActivity } from "../../../interfaces/employee-schedule/i-volunteer-acitivty";
import { IValidateCustomElement } from "../../../interfaces/i-validate-custom-element";

@autoinject
export class SignSubmit {
    public controller: DialogController;
    public validationController: ValidationController;
    public options: ISignSubmitOptions = {
        title: "Information Verified",
        description: "All the information has been verified.",
        type: "sign-submit",
        icon: "fas fa-check",
        isDocument: true,
        width: "w-50"
    };
    public signature: boolean = false;
    public dateSignedOn: string = "";
    public timeSignedOn: string = "";
    public datePickerOptions: DatepickerOptions = {
        endDate: moment().format("MM/DD/YYYY")
    };
    public maxLength: number = 110;
    public isShowCompleteHopeAttestationDescription: boolean = false;
    public hopeAttestationDescription: string =
        " I certify that the accompanying information accurately reflects patient assessment information for this patient and that I collected or coordinated collection of this information on the dates specified. To the best of my knowledge, this information was collected in accordance with applicable Medicare and Medicaid Requirements. I understand that reporting this information is used as a basis for payment from federal funds. I further understand that failure to report such information may lead to a payment reduction in the Fiscal Year payment determination. I also certify that I am authorized to submit this information by this provider on its behalf.";
    public clockIconBackgroundColor: string = "white";
    public volunteerSignature: boolean = false;
    public isVolunteerTask: boolean = false;
    public volunteerActivityLists: IGetCustomVolunteerActivity[] = [];
    public selectedVolunteerActivity: IGetCustomVolunteerActivity = null;
    public volunteerActivityValidation: IValidateCustomElement = {
        required: true,
        displayName: "Volunteer Activity Type",
        matches: true
    };

    @computedFrom(nameof<SignSubmit>("isShowCompleteHopeAttestationDescription"))
    public get hopeAttestationShortDescription() {
        return this.hopeAttestationDescription.length > this.maxLength
            ? this.hopeAttestationDescription.substring(0, this.maxLength) + "..."
            : this.hopeAttestationDescription;
    }

    public constructor(controller: DialogController, controllerFactory: ValidationControllerFactory) {
        this.controller = controller;
        this.validationController = controllerFactory.createForCurrentScope();
        this.validationController.validateTrigger = validateTrigger.changeOrBlur;
    }

    public activate(options: IDialogOptions) {
        if (options) {
            Object.assign(this.options, options);
        }

        if (this.options?.isVolunteerTask) {
            this.volunteerActivityLists = this.options.volunteerActivityLists;
            this.isVolunteerTask = this.options.isVolunteerTask;
        }
    }

    public attached() {
        this.initValidation();
    }

    private initValidation() {
        ValidationRules.ensure((x: SignSubmit) => x.dateSignedOn)
            .required()
            .when((x: SignSubmit) => x.options?.showDateSettings)
            .withMessage("Signature Date is required.")
            .ensure((x: SignSubmit) => x.timeSignedOn)
            .required()
            .when((x: SignSubmit) => x.options?.showTimeSettings)
            .withMessage("Signature Time is required.")
            .on(this);
    }

    public async completeDocument() {
        let res = await this.validationController.validate();
        if (res.valid) {
            let resultOutput;
            if (this.isVolunteerTask) {
                resultOutput = {
                    signature: this.signature,
                    signatureDate: this.dateSignedOn,
                    signatureTime: this.timeSignedOn,
                    volunteerActivity: this.selectedVolunteerActivity
                };
            } else {
                resultOutput = {
                    signature: this.signature,
                    signatureDate: this.dateSignedOn,
                    signatureTime: this.timeSignedOn
                };
            }
            this.controller.ok(resultOutput);
        }
    }

    public toggle() {
        this.isShowCompleteHopeAttestationDescription = !this.isShowCompleteHopeAttestationDescription;
    }
}

export interface ISignSubmitOptions extends IDialogOptions {
    inconsistencies?: number;
    warnings?: number;
    isDocument: boolean;
    showDateSettings?: boolean;
    showTimeSettings?: boolean;
    isHopeAttestation?: boolean;
    isVolunteerTask?: boolean;
    volunteerActivityLists?: IGetCustomVolunteerActivity[];
}
